import { useId, useMemo } from "react";
import QRCode from "qrcode";
import { fileUrl, clipUrl } from "@/lib/api";
import { PT, refKey } from "@/lib/layout";

function qrPath(data, x, y, size) {
  try {
    const q = QRCode.create(data || " ", { errorCorrectionLevel: "M" });
    const n = q.modules.size, cell = size / n;
    let d = "";
    for (let r = 0; r < n; r++)
      for (let c = 0; c < n; c++) if (q.modules.get(r, c)) d += `M${x + c * cell} ${y + r * cell}h${cell}v${cell}h-${cell}z`;
    return d;
  } catch {
    return "";
  }
}

function Border({ layout, color }) {
  const b = layout.border || {};
  if (!b.style || b.style === "none") return null;
  const W = layout.width_mm, H = layout.height_mm, bw = (b.width_pt || 1) * PT;
  const insets = [0.3 + bw / 2];
  if (b.style === "double") insets.push(insets[0] + bw + 0.7);
  return insets.map((i, k) =>
    layout.shape === "round" || layout.shape === "oval" ? (
      <ellipse key={k} cx={W / 2} cy={H / 2} rx={W / 2 - i} ry={H / 2 - i} fill="none" stroke={color} strokeWidth={bw} />
    ) : (
      <rect key={k} x={i} y={i} width={W - 2 * i} height={H - 2 * i} rx={b.style === "rounded" ? 1.6 : 0} fill="none" stroke={color} strokeWidth={bw} />
    )
  );
}

function TextEl({ el, color, uidp, idx }) {
  const s = el.size_pt * PT;
  const common = {
    fill: color, fontFamily: `"hs-${el.font}"`, fontSize: s, fontWeight: el.bold ? 700 : 400,
    fontStyle: el.italic ? "italic" : "normal", letterSpacing: el.letter_spacing_mm || 0, className: "stamp-text",
  };
  if (el.curve) {
    const { cx, cy, r } = el;
    const d = el.curve === "top" ? `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}` : `M ${cx - r} ${cy} A ${r} ${r} 0 0 0 ${cx + r} ${cy}`;
    const pid = `${uidp}-arc-${idx}`;
    return (
      <>
        <path id={pid} d={d} fill="none" />
        <text {...common}>
          <textPath href={`#${pid}`} startOffset="50%" textAnchor="middle">{el.text}</textPath>
        </text>
      </>
    );
  }
  const anchor = el.align === "left" ? "start" : el.align === "right" ? "end" : "middle";
  return (
    <text {...common} x={el.x} y={el.y} textAnchor={anchor} textDecoration={el.underline ? "underline" : undefined}>
      {el.text}
    </text>
  );
}

export function StampSvg({ layout, color = "#111827", scale = 4, realistic = false, onElDown, selected, className = "", svgRef, showSafe = 0 }) {
  const uidp = useId().replace(/:/g, "");
  const W = layout.width_mm, H = layout.height_mm;
  const round = layout.shape === "round" || layout.shape === "oval";
  const qrs = useMemo(
    () => layout.elements.map((e) => (e.type === "qr" ? qrPath(e.data, e.x, e.y, e.size) : null)),
    [layout.elements]
  );
  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width={W * scale} height={H * scale} className={className} style={{ overflow: "visible" }}>
      <defs>
        <filter id={`${uidp}-tint`}>
          <feFlood floodColor={color} />
          <feComposite in2="SourceAlpha" operator="in" />
        </filter>
        <filter id={`${uidp}-rough`}>
          <feTurbulence type="fractalNoise" baseFrequency="2.4" numOctaves="2" seed="3" />
          <feDisplacementMap in="SourceGraphic" scale="0.18" />
        </filter>
        <clipPath id={`${uidp}-clip`}>
          {round ? <ellipse cx={W / 2} cy={H / 2} rx={W / 2} ry={H / 2} /> : <rect width={W} height={H} />}
        </clipPath>
      </defs>
      {round ? <ellipse cx={W / 2} cy={H / 2} rx={W / 2} ry={H / 2} fill="#fff" /> : <rect width={W} height={H} fill="#fff" />}
      {showSafe > 0 &&
        (round ? (
          <ellipse cx={W / 2} cy={H / 2} rx={W / 2 - showSafe} ry={H / 2 - showSafe} fill="none" stroke="#94a3b8" strokeWidth={0.12} strokeDasharray="0.6 0.5" />
        ) : (
          <rect x={showSafe} y={showSafe} width={W - 2 * showSafe} height={H - 2 * showSafe} fill="none" stroke="#94a3b8" strokeWidth={0.12} strokeDasharray="0.6 0.5" />
        ))}
      <g filter={realistic ? `url(#${uidp}-rough)` : undefined} opacity={realistic ? 0.9 : 1}>
        <Border layout={layout} color={color} />
        {layout.elements.map((el, i) => {
          const key = refKey(el.ref) || `e${i}`;
          const sel = selected && refKey(selected) === key;
          const down = onElDown ? (e) => onElDown(el.ref, e) : undefined;
          const style = onElDown ? { cursor: "move" } : undefined;
          let node = null;
          if (el.type === "text") node = <TextEl el={el} color={color} uidp={uidp} idx={i} />;
          else if (el.type === "qr") node = <path d={qrs[i]} fill={color} />;
          else if (el.type === "clipart" || el.type === "image")
            node = (
              <image href={el.type === "clipart" ? clipUrl(el.name) : fileUrl(el.src)} x={el.x} y={el.y} width={el.w} height={el.h}
                preserveAspectRatio="xMidYMid meet" filter={`url(#${uidp}-tint)`} />
            );
          return (
            <g key={key} data-ref={key} onPointerDown={down} style={style} data-selected={sel ? "1" : "0"}>
              {node}
            </g>
          );
        })}
      </g>
    </svg>
  );
}
